'use client';

import { notFound, useRouter } from 'next/navigation';
import { publicFeeLimits, controlledFeeVisas } from '@/lib/mock-data';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Briefcase, CalendarDays, MapPin, Sparkles, UserCheck, FileText, Share2, Users, ClipboardCheck, Wallet, UserRound, ArrowLeft, Image as ImageIcon, Milestone, Languages, Cake, ChevronsRight, Info, Star, GraduationCap, Weight, Ruler, Dna, User, Bookmark, BrainCircuit, Loader2, LogIn, UserPlus, Pencil, FastForward, ListChecks, HardHat, PlusCircle, MoreHorizontal, Copy, Eye, FileIcon, FileImageIcon, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { use, useState, useEffect } from 'react';
import { cn, convertTime, createSuggesstionJobsUrl, findVisaByVisaDetail, formatGender, formatSalaryForDisplay, formatVisa, generateBulletJobCrawl, generateHtmlFromMarkdown, getJobImage, getVisaBadgeClassName } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/components/../contexts/AuthContext';
import { AuthDialog } from '@/components/auth-dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/hooks/use-toast';
import { EditProfileDialog } from '@/app/ho-so-cua-toi/components/candidate-edit-dialog';
import { validateProfileForApplication } from '@/lib/validators';
import { CtaViecLamTuongTu } from '@/components/cta-viec-lam-tuong-tu';
import { applyJob, updateProfile } from '@/actions/user-action';
import { useServerInfo } from '@/components/layout/root-provider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useImagePreview } from '@/contexts/ImagePreviewContext';
import { JobDetailConsultant } from './components/consultant';
import { AutoHeightIframe } from '@/components/ui/auto-height-iframe';
import { useIsMobile } from '@/hooks/use-mobile';
import { revalidateTag } from 'next/cache';
import { findSuggestedJobs } from '@/actions/job-action';
import { generateJobFilter } from '@/lib/job-filter-util';

const JobDetailSection = ({ title, children, icon: Icon }: { title: string, children: React.ReactNode, icon: React.ElementType }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-3 font-headline text-xl"><Icon className="text-primary h-6 w-6" />{title}</CardTitle>
        </CardHeader>
        <CardContent className="prose max-w-none text-muted-foreground prose-sm md:prose-base">
            {children}
        </CardContent>
    </Card>
);

const JPY_VND_RATE = 180; // Example rate
const USD_VND_RATE = 26300; // Example rate


const formatCurrency = (value?: any, currency: 'JPY' | 'VND' | 'USD' = 'JPY') => {
    if (!value) return 'Liên hệ';
    const numericValue = typeof value === 'number' ? value : parseInt(value.replace(/[^0-9]/g, ''), 10);
    if (isNaN(numericValue)) return value;

    if (currency === 'VND') {
        return `${numericValue.toLocaleString('vi-VN')} VNĐ`;
    }
    if (currency === 'USD') {
        return `${numericValue.toLocaleString('en-US')} USD`;
    }
    return `${numericValue.toLocaleString('ja-JP')} yên`;
};


const convertCurrency = (value?: string, from: 'JPY' | 'USD' = 'JPY') => {
    if (!value) return null;
    const numericValue = parseInt(('' + value).replace(/[^0-9]/g, ''), 10);
    if (isNaN(numericValue)) return null;

    const rate = from === 'JPY' ? JPY_VND_RATE : USD_VND_RATE;
    const vndValue = numericValue * rate;
    return `≈ ${vndValue.toLocaleString('vi-VN')} VNĐ`;
};

const visasForVndDisplay = [
    'Thực tập sinh 3 năm',
    'Thực tập sinh 1 năm',
    'Đặc định đi mới',
    'Kỹ sư, tri thức đầu Việt',
];

export default function JobDetailClientPage({ job }: { job: any }) {
    const isMobile = useIsMobile();
    const { toast } = useToast();
    const { serverTime } = useServerInfo();
    const { setImagePreview } = useImagePreview();
    const { isLoggedIn, setPostLoginAction, user, setApplicationCount, setSavedJobCount, setLastAction, role } = useAuth();
    const [isClient, setIsClient] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [hasApplied, setHasApplied] = useState(false);
    const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
    const [isConfirmLoginOpen, setIsConfirmLoginOpen] = useState(false);
    const [isProfileIncompleteAlertOpen, setIsProfileIncompleteAlertOpen] = useState(false);
    const [isProfileEditDialogOpen, setIsProfileEditDialogOpen] = useState(false);
    const [postedTime, setPostedTime] = useState<string | null>(null);
    const [interviewDate, setInterviewDate] = useState<string | null>(null);
    const jobTitle = generateBulletJobCrawl(job);
    const htmlForm = generateHtmlFromMarkdown(job.visa, job.formMarkdownArray);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [behavioralSuggestions, setBehavioralSuggestions] = useState<any[]>([]);
    const [isLoadingSuggesstions, setIsLoadingSuggesstions] = useState(true);
    const [suggestedJobsUrl, setSuggestedJobsUrl] = useState<string | null>(null);
    useEffect(() => {
        setIsClient(true);
        const savedJobs = JSON.parse(localStorage.getItem('savedJobs') || '[]');
        setIsSaved(savedJobs.includes(job.id));
        // Safely calculate dates on the client to avoid hydration mismatch
        setPostedTime(convertTime(job?.postedDate || job?.time || job?.createdDate));

        if (!!job.interviewDay) {
            setInterviewDate(job.interviewDay);
        }
        const queryParams = createSuggesstionJobsUrl(job);
        const { newFilters } = generateJobFilter(queryParams);
        setSuggestedJobsUrl(`/tim-viec-lam?${queryParams.toString()}`);
        findSuggestedJobs(job.id, newFilters).then(async data => {
            // const docs = data.docs ?? [];
            // if (data.total < 4 && !!newFilters.workLocation?.length) {
            //     delete newFilters.workLocation;
            //     const { docs: additionalDocs } = await findSuggestedJobs(job.id, newFilters);
            //     setBehavioralSuggestions([...docs, ...additionalDocs]);
            // } else {
            //     setBehavioralSuggestions(data.docs);
            // }
            setBehavioralSuggestions(data.docs);
            setIsLoadingSuggesstions(false);
        })
    }, []);

    useEffect(() => {
        if (!!user) {
            const appliedJobs = user.appliedJobs || [];
            setHasApplied(appliedJobs.includes(job.id));
        }
    }, [user])

    const handleSaveJob = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const savedJobs = JSON.parse(localStorage.getItem('savedJobs') || '[]');
        if (isSaved) {
            const newSavedJobs = savedJobs.filter((id: string) => id !== job.id);
            localStorage.setItem('savedJobs', JSON.stringify(newSavedJobs));
            setIsSaved(false);
            setSavedJobCount(prev => (prev - 1) < 0 ? 0 : prev - 1);
        } else {
            savedJobs.push(job.id);
            localStorage.setItem('savedJobs', JSON.stringify(savedJobs));
            setIsSaved(true);
            setSavedJobCount(prev => prev + 1);
            setLastAction('saved');
        }
        window.dispatchEvent(new Event('storage'));
    };

    const handleApplyClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isLoggedIn) {
            setPostLoginAction({ type: 'APPLY_JOB', data: { jobId: job.id, jobTitle: jobTitle } });
            setIsConfirmLoginOpen(true);
        } else {
            if (validateProfileForApplication(user) && !!user) {
                const appliedJobs = user.appliedJobs || [];
                appliedJobs.push(job.id);
                await applyJob(user.uid, job);
                await updateProfile(user.uid, { appliedJobs });
                user.appliedJobs = Object.assign([], appliedJobs);
                setHasApplied(true);
                setApplicationCount(prev => prev + 1);
                setLastAction('applied');
                toast({
                    title: 'Ứng tuyển thành công!',
                    description: `Hồ sơ của bạn đã được gửi cho công việc "${jobTitle}".`,
                    className: 'bg-green-500 text-white'
                });
            } else {
                setIsProfileIncompleteAlertOpen(true);
            }
        }
    };

    const handleConfirmLogin = () => {
        setIsConfirmLoginOpen(false);
        setIsAuthDialogOpen(true);
    };

    const handleConfirmUpdateProfile = () => {
        setIsProfileIncompleteAlertOpen(false);
        setIsProfileEditDialogOpen(true);
    };

    const getFeeDisplay = (feeValue: any | undefined, feeLabel: string) => {
        const isControlled = controlledFeeVisas.includes(job.visa || '');
        if (!feeValue) {
            return isControlled ? "Không rõ" : null;
        }
        const feeLimit = publicFeeLimits[job.visa as keyof typeof publicFeeLimits];

        const numericFee = parseInt(feeValue);
        if (isControlled && numericFee > feeLimit) {
            return "Không rõ";
        }
        return `${formatCurrency(feeValue, 'USD')} (${convertCurrency(feeValue, 'USD')})`;
    };

    const RequirementItem = ({ icon: Icon, label, value, className }: { icon: React.ElementType, label: string, value?: string | number | null, className?: string }) => {
        if (!value) return null;
        return (
            <div className={cn("flex items-start gap-3", className)}>
                <Icon className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                <div>
                    <p className="font-semibold text-foreground">{label}</p>
                    <p className="text-muted-foreground">{value}</p>
                </div>
            </div>
        )
    };
    if (typeof job === 'undefined') {
        return <div className="flex justify-center items-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    }

    const openImageViewer = (imageUrl: string) => {
        setImagePreview(imageUrl);
    };
    const applyButtonContent = hasApplied ? 'Đã ứng tuyển' : 'Ứng tuyển ngay';

    const feeWithTuitionDisplay = getFeeDisplay(job.fee, "Phí và vé và học phí");
    const feeDisplay = getFeeDisplay(job.fee, job.visa?.includes('Thực tập sinh') ? "Phí và vé không học phí" : "Phí có vé");
    const feeNoTicketDisplay = getFeeDisplay(job.netFeeNoTicket, "Phí không vé");
    const avatar = job.avatar || getJobImage(job.job, job.career);
    const badgeClassName = getVisaBadgeClassName(job.visa);
    let isExpired = false;
    if (job?.expiredDate < serverTime) {
        isExpired = true;
    }
    let annualIncome = null;
    if (job.basicSalary > 100000 && job.basicSalary < 900000) {
        annualIncome = job.basicSalary * 12;
    }

    const handleConvertToPDF = async () => {
        if (!htmlForm) {
            toast({
                variant: "destructive",
                title: "Không có dữ liệu HTML"
            });
            return;
        }

        try {
            setIsGeneratingPdf(true);
            const response = await fetch('/api/public/getJobPDF', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: job.id, htmlContent: htmlForm, responseType: 'application/pdf' }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Lỗi từ server: ${response.status} ${errorText}`);
            }
            try {
                revalidateTag(`job-detail-${job.id}`);
            } catch (error) {

            }

            const blob = await response.blob();
            const fileName = `${jobTitle} | ${job.id} | ${job.code}`;

            // Create a link to download the PDF
            if (isMobile &&
                navigator.canShare &&
                navigator.canShare({ files: [new File([], "")] })) {
                const file = new File([blob], `${fileName}.pdf`, {
                    type: "application/pdf",
                });
                try {
                    await navigator.share({
                        files: [file],
                        title: fileName,
                        text: `Lưu hoặc tải về PDF đơn hàng: ${fileName}`
                    });
                } catch (error) {
                    console.error("Error sharing image:", error);
                }
            } else {
                const url = URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `${fileName}.pdf`;
                a.click();

                URL.revokeObjectURL(url);
                toast({ title: "Tải PDF thành công", className: "bg-green-500 text-white" });
            }

        } catch (e) {
            console.error("PDF Generation Error:", e);
            let errorMessage = "Không thể tạo file PDF. Vui lòng thử lại.";
            if (e instanceof Error) {
                errorMessage = e.message;
            }
            toast({
                variant: "destructive",
                title: "Lỗi khi tải PDF về.",
                description: errorMessage
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    }
    const handleConvertToImage = async () => {
        if (!htmlForm) {
            toast({
                variant: "destructive",
                title: "Không có dữ liệu HTML."
            });
            return;
        }

        try {
            setIsGeneratingPdf(true);
            if (job.formImageHJ) {
                setImagePreview(job.formImageHJ);
            } else {
                const response = await fetch('/api/public/getJobPDF', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ id: job.id, htmlContent: htmlForm, responseType: 'image/jpeg' }),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Lỗi từ server: ${response.status} ${errorText}`);
                }
                try {
                    revalidateTag(`job-detail-${job.id}`);
                } catch (error) {

                }

                const pdfBlob = await response.blob();

                // Create a link to download the PDF
                const url = window.URL.createObjectURL(pdfBlob);
                setImagePreview(url);
            }
            // const link = document.createElement('a');
            // link.href = url;
            // link.download = `${jobTitle} | ${job.id} | ${job.code}.jpg`;
            // document.body.appendChild(link);
            // link.click();
            // document.body.removeChild(link);
            // window.URL.revokeObjectURL(url);

            // toast({
            //     title: "Tạo Ảnh thành công",
            //     description: "File Ảnh đã được tải xuống.",
            //     className: 'bg-green-500 text-white'
            // });

        } catch (e) {
            console.error("Image Generation Error:", e);
            let errorMessage = "Không thể tạo file Ảnh. Vui lòng thử lại.";
            if (e instanceof Error) {
                errorMessage = e.message;
            }
            toast({
                variant: "destructive",
                title: "Lỗi khi tải Ảnh về.",
                description: errorMessage
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    }

    if (!!job) {
        return (
            <>
                <div className="bg-secondary">
                    {/* {job && <JsonLdScript job={job} appliedFilters={appliedFilters} />} */}
                    <div className="container mx-auto px-4 md:px-6 py-12">
                        <div className="mb-6">
                            <Button asChild variant="outline" size="sm">
                                <Link href="/tim-viec-lam"><ArrowLeft className="mr-2 h-4 w-4" />Quay lại trang Việc làm</Link>
                            </Button>
                        </div>
                        <div className="grid lg:grid-cols-3 gap-8 items-start">
                            {/* Main Content */}
                            <div className={cn("lg:col-span-2 space-y-6", isExpired && "grayscale")}>
                                <Card id="VLCT-HEADER" className="shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div>
                                            <h1 className="text-2xl md:text-3xl font-bold font-headline">{jobTitle}</h1>
                                            <div id="IDVLCT01" className="flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-xs font-bold text-white w-fit my-3">
                                                <Image src="/img/japanflag.png" alt="Japan flag" width={16} height={16} className="h-3 w-auto" />
                                                <span>{job.code}</span>
                                            </div>
                                            {isClient && job.visa && (
                                                <Badge
                                                    variant="outline"
                                                    className={cn("mt-3 w-fit", badgeClassName)}
                                                >
                                                    {formatVisa(job.visa)}
                                                </Badge>
                                            )}
                                            <div className="flex flex-wrap gap-x-6 gap-y-2 text-muted-foreground mt-3">
                                                {job.workLocation && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {job.workLocation}</p>}
                                                <p className="flex items-center gap-2">
                                                    <span className="text-primary font-medium">Ngày phỏng vấn:</span>
                                                    <span>{interviewDate || "Lịch phỏng vấn linh hoạt"}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="mt-6 flex flex-col sm:flex-row items-baseline justify-between gap-4">
                                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                                {isClient ? (
                                                    <>
                                                        {role !== 'admin' && <>
                                                            <Button size="lg" variant="outline" className={cn("w-full sm:w-auto text-base", isSaved ? "border-accent-orange text-accent-orange bg-accent-orange/5" : "border-gray-300")} onClick={handleSaveJob}>
                                                                <Bookmark className={cn("mr-2", isSaved && "fill-current text-accent-orange")} />
                                                                {isSaved ? 'Việc đã lưu' : 'Lưu việc làm'}
                                                            </Button>
                                                            <Button size="lg" className="w-full sm:w-auto bg-accent-orange text-white text-base" onClick={handleApplyClick} disabled={hasApplied || isExpired}>
                                                                {isExpired ? 'Đã hết hạn' : applyButtonContent}
                                                            </Button>
                                                        </>}
                                                        {role === 'admin' && <>
                                                            <Button size="lg" className="w-full sm:w-auto text-base" onClick={() => setPostLoginAction({ type: 'QUICK_EDIT_JOB', data: job })}>
                                                                <Pencil className="mr-2" />
                                                                Sửa việc làm
                                                            </Button>
                                                            <Button size="lg" variant={'destructive'} className="w-full sm:w-auto text-base" onClick={() => setPostLoginAction({ type: 'REQUEST_CLOSE_JOB', data: { id: job.id } })}>
                                                                <X className="mr-2" />
                                                                Đóng việc làm
                                                            </Button>
                                                        </>
                                                        }
                                                    </>
                                                ) : (
                                                    <>
                                                        <Skeleton className="h-11 w-full sm:w-40" />
                                                        <Skeleton className="h-11 w-full sm:w-40" />
                                                    </>
                                                )}
                                            </div>
                                            <div className="w-full text-right">
                                                <p className="text-xs text-muted-foreground flex items-center justify-end gap-2">
                                                    <span className="text-primary font-medium">Đăng lúc:</span>
                                                    <span>{postedTime || "..."}</span>
                                                </p>
                                            </div>
                                        </div>

                                        {isExpired && (
                                            <Alert variant="destructive" className="mt-4">
                                                <Info className="h-4 w-4" />
                                                <AlertTitle className="font-bold">Việc làm đã hết hạn</AlertTitle>
                                                <AlertDescription>
                                                    Tin tuyển dụng này đã hết hạn. Bạn có thể tham khảo các việc làm tương tự bên dưới.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                    </div>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-3 font-headline text-xl"><Info className="text-primary h-6 w-6" />Thông tin cơ bản</CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                        {!!job.visa && <RequirementItem icon={Milestone} label="Loại Visa" value={findVisaByVisaDetail(job.visa)} />}
                                        {!!job.visa && <RequirementItem icon={ChevronsRight} label="Chi tiết Visa" value={job.visa} />}
                                        {!!job.job && <RequirementItem icon={Briefcase} label="Ngành nghề" value={job.job ?? job.career ?? 'Liên hệ'} />}
                                        {!!job.interviewLocation && <RequirementItem icon={MapPin} label="Nơi phỏng vấn" value={job.interviewLocation ?? 'Liên hệ'} />}
                                        {!!job.gender && <RequirementItem icon={User} label="Giới tính" value={job.gender ? formatGender(job.gender) : 'Liên hệ'} />}
                                        {!!job.numberRecruits && <RequirementItem icon={Users} label="Số lượng" value={job.numberRecruits ? `${job.numberRecruits} người` : null} />}
                                        {!!(job.minAge || job.maxAge) && <RequirementItem icon={Cake} label="Yêu cầu tuổi" value={job.minAge && job.maxAge ? `${job.minAge} - ${job.maxAge}` : job.minAge ? `Từ ${job.minAge}` : job.maxAge ? `Đến ${job.maxAge}` : null} />}
                                        {!!job.languageLevel && <RequirementItem icon={Languages} label="Yêu cầu ngoại ngữ" value={job.languageLevel ?? 'Không rõ'} />}
                                        <RequirementItem icon={CalendarDays} label="Ngày phỏng vấn" value={interviewDate ? interviewDate : 'Linh hoạt'} />
                                        {!!job.interviewRounds && <RequirementItem icon={ClipboardCheck} label="Số vòng" value={job.interviewRounds ? `${job.interviewRounds} vòng` : null} />}
                                        {!!feeWithTuitionDisplay && <RequirementItem icon={Wallet} label="Phí và vé và học phí" value={feeWithTuitionDisplay ?? 'Liên hệ'} />}
                                        {!!feeDisplay && <RequirementItem icon={Wallet} label={job.visa?.includes('Thực tập sinh') ? "Phí và vé không học phí" : "Phí có vé"} value={feeDisplay} />}
                                        {!!feeNoTicketDisplay && <RequirementItem icon={Wallet} label="Phí không vé" value={feeNoTicketDisplay ?? 'Liên hệ'} />}
                                        {!!(job.specialConditions) && <RequirementItem icon={Star} label="Điều kiện đặc biệt" value={job.specialConditions} />}
                                    </CardContent>
                                </Card>

                                {(job.educationLevel || job.experience || job.experienceYear || job.height || job.weight || job.vgb || job.haveTattoo || job.vision || job.interviewFormat) && <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-3 font-headline text-xl"><UserCheck className="text-primary h-6 w-6" />Yêu cầu chi tiết</CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                        {job.educationLevel && <RequirementItem icon={GraduationCap} label="Yêu cầu học vấn" value={job.educationLevel ?? 'Không rõ'} />}
                                        {job.experience && <RequirementItem icon={Briefcase} label="Kinh nghiệm" value={job.experience ?? 'Không rõ'} />}
                                        {job.experienceYear && <RequirementItem icon={CalendarDays} label="Số năm kinh nghiệm" value={job.experienceYear ?? 'Không rõ'} />}
                                        {job.height && <RequirementItem icon={Ruler} label="Chiều cao" value={job.height ?? 'Không rõ'} />}
                                        {job.weight && <RequirementItem icon={Weight} label="Cân nặng" value={job.weight ?? 'Không rõ'} />}
                                        {job.vgb && <RequirementItem icon={Dna} label="Viêm gan B" value={job.vgb ?? 'Không rõ'} />}
                                        {job.haveTattoo && <RequirementItem icon={User} label="Hình xăm" value={job.haveTattoo ?? 'Không rõ'} />}
                                        {job.vision && <RequirementItem icon={ImageIcon} label="Yêu cầu thị lực" value={job.vision ?? 'Không rõ'} />}
                                        {job.interviewFormat && <RequirementItem icon={ClipboardCheck} label="Hình thức phỏng vấn" value={job.interviewFormat ?? 'Không rõ'} />}
                                    </CardContent>
                                </Card>}

                                <JobDetailSection title="Mô tả công việc & Ghi chú" icon={FileText}>
                                    {/* <div>
                                        <p>Mô tả chi tiết cho công việc {(job.career || job.job) && <strong>{job.job ?? job.career}, {job.workLocation}{job.numberRecruits ? `, tuyển ${job.numberRecruits} ${job.gender ? formatGender(job.gender) : 'người'}` : ''}</strong>}
                                            . Đây là cơ hội tuyệt vời để làm việc trong một môi trường chuyên nghiệp tại Nhật Bản
                                            . Công việc đòi hỏi sự cẩn thận, tỉ mỉ và trách nhiệm cao để đảm bảo chất lượng sản phẩm tốt nhất.</p>
                                        <ul>
                                            {(job.career || job.job) && <li>Chi tiết công việc: {job.job ?? job.career}.</li>}
                                            <li>Môi trường làm việc sạch sẽ, hiện đại.</li>
                                        </ul>
                                    </div> */}
                                    <div>
                                        <p>{job.aiContent}</p>
                                        {role === 'admin' && <>
                                            <p>-----------------</p>
                                            <p>{job.baseContent}</p>
                                        </>
                                        }
                                    </div>
                                </JobDetailSection>

                                <JobDetailSection title="Quyền lợi & Chế độ" icon={Sparkles}>
                                    {job.benefits && <div dangerouslySetInnerHTML={{ __html: job.benefits }} />}
                                    {!job.benefits && <div><ul><li>Hưởng đầy đủ chế độ bảo hiểm (y tế, hưu trí, thất nghiệp) theo quy định của pháp luật Nhật Bản.</li><li>Hỗ trợ chi phí nhà ở và đi lại.</li><li>Có nhiều cơ hội làm thêm giờ để tăng thu nhập.</li><li>Được đào tạo bài bản và có cơ hội phát triển, gia hạn hợp đồng lâu dài.</li><li>Thưởng 1-2 lần/năm tùy theo kết quả kinh doanh.</li></ul></div>}
                                </JobDetailSection>


                                {!!htmlForm &&
                                    <JobDetailSection title="Thông Báo Đơn Hàng" icon={ImageIcon}>
                                        <div className={cn("space-y-6", isExpired && "grayscale")}>
                                            <>
                                                <div>
                                                    <AutoHeightIframe htmlContent={htmlForm} title={jobTitle} />
                                                    <div className='flex justify-end items-center mt-2 flex-wrap gap-2'>
                                                        <Button onClick={handleConvertToImage} disabled={isGeneratingPdf} className="w-full md:w-[40%] flex bg-accent-orange text-white hover:bg-accent-orange/90" style={{ fontSize: '15px' }}>
                                                            {isGeneratingPdf ? 'Đang tạo ảnh...' : (
                                                                <>
                                                                    <FileImageIcon className="mr-2 h-4 w-4" />
                                                                    Xem và tải ảnh Form đơn hàng
                                                                </>
                                                            )}
                                                        </Button>
                                                        <Button onClick={handleConvertToPDF} disabled={isGeneratingPdf} className='w-full md:w-[40%] flex' style={{ fontSize: '15px' }}>
                                                            {isGeneratingPdf ? 'Đang tạo PDF...' : (
                                                                <>
                                                                    <FileIcon className="mr-2 h-4 w-4" />
                                                                    Xem và tải PDF Form đơn hàng
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </>
                                        </div>
                                    </JobDetailSection>
                                }

                                <JobDetailSection title="Hình ảnh công việc" icon={ImageIcon}>
                                    <div className={cn("space-y-6", isExpired && "grayscale")}>
                                        <div className={cn("grid grid-cols-1 gap-4", job.formImage && role === 'admin' ? 'md:grid-cols-2' : 'md:grid-cols-1')}>
                                            <div onClick={() => openImageViewer(avatar)} className={cn("relative overflow-hidden rounded-lg border-2 border-[#9B999A] group cursor-pointer", job.formImage && role === 'admin' ? 'aspect-[2/3]' : 'aspect-[5/3]')}>
                                                <Image id={job.code} src={avatar} alt={job.code} fill objectFit='contain' className="object-cover" quality={100} unoptimized />
                                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <Eye className="h-10 w-10 text-white" />
                                                </div>
                                            </div>
                                            {job.formImage && role === 'admin' &&
                                                <div onClick={() => openImageViewer(job.formImage)} className={cn("relative overflow-hidden rounded-lg border-2 border-[#9B999A] group cursor-pointer aspect-[2/3]")}>
                                                    <Image id={job.code} src={job.formImage} alt={job.code} fill objectFit='contain' quality={100} unoptimized />
                                                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <Eye className="h-10 w-10 text-white" />
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </JobDetailSection>

                            </div>

                            {/* Sidebar */}
                            <aside className={cn("lg:col-span-1 space-y-6 lg:sticky lg:top-24", isExpired && "grayscale")}>
                                <Card className="shadow-lg">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Mức lương</CardTitle>
                                    </CardHeader>

                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <p className="text-sm text-muted-foreground">Lương cơ bản</p>
                                            <p className="text-2xl font-bold text-accent-green">{formatSalaryForDisplay(job.basicSalary, formatVisa(job.visa))}</p>
                                            {job.basicSalary > 0 && <p className="text-xs text-muted-foreground">{convertCurrency(job.basicSalary, 'JPY')}</p>}
                                            {job.realSalary > 0 && (
                                                <div className="pt-2">
                                                    <p className="font-semibold text-muted-foreground">Thực lĩnh: ~{formatSalaryForDisplay(job.realSalary, formatVisa(job.visa))}</p>
                                                    {job.realSalary && <p className="text-xs text-muted-foreground">{convertCurrency(job.realSalary, 'JPY')}</p>}
                                                </div>
                                            )}
                                        </div>
                                        <div className="border-t pt-4 space-y-2 text-sm">
                                            {!!annualIncome && <p>Thu nhập năm: Khoảng <strong>{formatSalaryForDisplay(annualIncome, formatVisa(job.visa))}</strong></p>}
                                            {job.annualBonus && <p>Thưởng: <strong>{job.annualBonus}</strong></p>}
                                        </div>
                                    </CardContent>
                                </Card>
                                <JobDetailConsultant job={job} />
                            </aside>
                        </div>
                    </div>
                    <AuthDialog isOpen={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen} />
                    <AlertDialog open={isConfirmLoginOpen} onOpenChange={setIsConfirmLoginOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Bạn chưa đăng nhập</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Bạn cần đăng nhập để ứng tuyển, bạn có muốn đăng nhập không?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Để sau</AlertDialogCancel>
                                <AlertDialogAction onClick={handleConfirmLogin}>Đồng ý</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog open={isProfileIncompleteAlertOpen} onOpenChange={setIsProfileIncompleteAlertOpen}>
                        <AlertDialogContent id="UNGTUYEN-L02-B1">
                            <AlertDialogHeader>
                                <AlertDialogTitle>Hồ sơ của bạn chưa hoàn thiện</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Để có thể ứng tuyển, bạn cần cập nhật đủ thông tin cá nhân và cung cấp ít nhất một phương thức liên lạc (SĐT, Zalo...). Bạn có muốn cập nhật hồ sơ ngay bây giờ không?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Để sau</AlertDialogCancel>
                                <AlertDialogAction onClick={handleConfirmUpdateProfile}>Đồng ý, cập nhật</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    <EditProfileDialog
                        isOpen={isProfileEditDialogOpen}
                        onOpenChange={setIsProfileEditDialogOpen}
                        onSaveSuccess={() => {
                            toast({
                                title: 'Cập nhật thành công!',
                                description: 'Thông tin của bạn đã được lưu. Giờ bạn có thể ứng tuyển.',
                                className: 'bg-green-500 text-white'
                            });
                        }}
                        source="application"
                    />
                </div>
                <div className="space-t-20 md:space-t-28 pt-20 md:pt-28">
                    <CtaViecLamTuongTu isLoading={isLoadingSuggesstions} suggestions={behavioralSuggestions} viewAllUrl={suggestedJobsUrl} />
                </div>
            </>
        );
    }

}